export const DOCUMENT_STATUSES = ['draft', 'done', 'cancelled'] as const;

export type DocumentStatus = (typeof DOCUMENT_STATUSES)[number];

export type InvoiceStatus =
  | 'nothing_to_invoice'
  | 'to_invoice'
  | 'partially_invoiced'
  | 'fully_invoiced';

export type DeliveryStatus =
  | 'nothing_to_deliver'
  | 'to_deliver'
  | 'partially_delivered'
  | 'fully_delivered';

export type PaymentStatus = 'unpaid' | 'partially_paid' | 'paid' | 'overpaid';
export type CustomerResponse = 'pending' | 'accepted' | 'declined';

export type DerivedDocumentStatus =
  | InvoiceStatus
  | DeliveryStatus
  | PaymentStatus
  | CustomerResponse
  | 'sent'
  | 'expired';

export type StatusBadgeValue = DocumentStatus | DerivedDocumentStatus;

export interface QuantityProgressLine {
  quantity: number;
  completedQuantity: number;
}

export type DateValue = Date | string | number | null | undefined;

const LEGACY_STATUS_MAP: Readonly<Record<string, DocumentStatus>> = {
  draft: 'draft',
  'not processed': 'draft',
  sent: 'draft',
  'awaiting approval': 'draft',
  approved: 'done',
  approvedforsending: 'done',
  'approved for sending': 'done',
  accepted: 'done',
  assigned: 'done',
  shipped: 'done',
  delivered: 'done',
  invoiced: 'done',
  converted: 'done',
  'awaiting payment': 'done',
  paid: 'done',
  posted: 'done',
  done: 'done',
  declined: 'cancelled',
  void: 'cancelled',
  deleted: 'cancelled',
  cancelled: 'cancelled',
  canceled: 'cancelled',
};

const normalizeKey = (value: string): string => value.trim().toLowerCase().replace(/[\s_-]+/g, ' ');

/** Returns true only for canonical, lowercase persisted statuses. */
export function isDocumentStatus(value: unknown): value is DocumentStatus {
  return typeof value === 'string' && DOCUMENT_STATUSES.includes(value as DocumentStatus);
}

/**
 * Normalizes canonical and known legacy status values. Unknown values return the
 * supplied fallback so callers never accidentally persist an unsupported state.
 */
export function normalizeDocumentStatus(
  value: unknown,
  fallback: DocumentStatus = 'draft',
): DocumentStatus {
  if (isDocumentStatus(value)) return value;
  if (typeof value !== 'string') return fallback;
  return LEGACY_STATUS_MAP[normalizeKey(value)] ?? fallback;
}

export function canTransitionDocumentStatus(
  from: DocumentStatus,
  to: DocumentStatus,
  options: { isAdmin?: boolean; hasDownstreamDoneDocument?: boolean } = {},
): boolean {
  if (from === to) return true;
  if (from === 'draft') return to === 'done' || to === 'cancelled';
  if (from === 'done') return to === 'cancelled' && !options.hasDownstreamDoneDocument;
  return to === 'draft' && options.isAdmin === true;
}

const finiteNonNegative = (value: number): number =>
  Number.isFinite(value) ? Math.max(0, value) : 0;

const sumQuantityProgress = (lines: readonly QuantityProgressLine[]) =>
  lines.reduce(
    (total, line) => ({
      quantity: total.quantity + finiteNonNegative(line.quantity),
      completed: total.completed + finiteNonNegative(line.completedQuantity),
    }),
    { quantity: 0, completed: 0 },
  );

export function deriveInvoiceStatus(lines: readonly QuantityProgressLine[]): InvoiceStatus {
  const { quantity, completed } = sumQuantityProgress(lines);
  if (quantity === 0) return 'nothing_to_invoice';
  if (completed === 0) return 'to_invoice';
  return completed >= quantity ? 'fully_invoiced' : 'partially_invoiced';
}

export function deriveDeliveryStatus(lines: readonly QuantityProgressLine[]): DeliveryStatus {
  const { quantity, completed } = sumQuantityProgress(lines);
  if (quantity === 0) return 'nothing_to_deliver';
  if (completed === 0) return 'to_deliver';
  return completed >= quantity ? 'fully_delivered' : 'partially_delivered';
}

export function derivePaymentStatus(totalAmount: number, allocatedAmount: number): PaymentStatus {
  const total = finiteNonNegative(totalAmount);
  const allocated = finiteNonNegative(allocatedAmount);

  if (allocated > total) return 'overpaid';
  if (total === 0 || allocated >= total) return 'paid';
  if (allocated === 0) return 'unpaid';
  return 'partially_paid';
}

export function isDocumentExpired(
  expiryDate: DateValue,
  status: DocumentStatus,
  today: Date = new Date(),
): boolean {
  if (status !== 'draft' || expiryDate == null || expiryDate === '') return false;

  const expiry = expiryDate instanceof Date ? new Date(expiryDate) : new Date(expiryDate);
  if (Number.isNaN(expiry.getTime())) return false;

  const comparisonDate = new Date(today);
  expiry.setHours(0, 0, 0, 0);
  comparisonDate.setHours(0, 0, 0, 0);
  return expiry < comparisonDate;
}
